import { getFormattedDateAndTime } from '@svs/nova-poolbetting/core/lib';
import { createDateTime } from '@svs/nova-poolbetting/schema/frameworks';
import { eachDayOfInterval, endOfISOWeek, format, startOfISOWeek } from 'date-fns';
import { sv } from 'date-fns/locale/sv';

export const locale = sv;

/**
 * Get the date range for the current month, including the days from the previous and next month that are displayed in the calendar.
 * This is used to determine which dates should be disabled based on the valid dates fetched from the server.
 *
 * @param date The date for which to get the date range (the month and year will be used).
 * @returns An array of Date objects representing the date range for the current month, including the days from the previous and next month that are displayed in the calendar.
 */
export function getDateRangeForCurrentMonth(date: Date): Date[] {
  const startDate = new Date(date.getFullYear(), date.getMonth() - 1);
  const endDate = new Date(date.getFullYear(), date.getMonth() + 2, 0);

  const startOfWeekDate = startOfISOWeek(startDate);
  const endOfWeekDate = endOfISOWeek(endDate);

  return eachDayOfInterval({ start: startOfWeekDate, end: endOfWeekDate });
}

/**
 * Format date to a readable string in Swedish locale, e.g. "Söndag, 3 maj 2026".
 *
 * @param date The date to format.
 * @returns The formatted date string.
 */
export function prettyDate(date: Date): string {
  const dateTime = createDateTime(date);
  const string = getFormattedDateAndTime(dateTime, {
    dateFormatOptions: 'dayAsTextCommaDayAsDateMonthAsTextAndYear',
    formatOptions: 'dateOnly',
    shouldCapitalizeFirstLetter: true,
  });
  return string;
}

/**
 * Get the local month name for a given date, e.g. "januari" for January.
 *
 * @param date The date for which to get the month name.
 * @returns The local month name for the given date.
 */
export function getLocalMonthName(date: Date): string {
  return format(date, 'LLLL', { locale: locale });
}
